/**
 * Восстановлено из бандла: объект Ge и хелпер St. Имена методов и формы
 * запросов оставлены как были — по ним и написан бэкенд.
 */

export const COLUMN_TYPES = [
  'Any', 'Text', 'Numeric', 'Int', 'Bool', 'Date', 'DateTime', 'Choice',
] as const;

export type ColumnType = (typeof COLUMN_TYPES)[number];

export type BaseRole = 'admin' | 'editor' | 'viewer' | 'none';
export type AccessLevel = 'owner' | 'editor' | 'viewer' | 'none';

export const BASE_ROLES: BaseRole[] = ['admin', 'editor', 'viewer', 'none'];

export const ROLE_LABELS: Record<BaseRole, string> = {
  admin: 'Администратор',
  editor: 'Редактор',
  viewer: 'Просмотр',
  none: 'Без доступа',
};

export const ROLE_HINTS: Record<BaseRole, string> = {
  admin: 'структура, доступы, удаление',
  editor: 'правка данных и таблиц',
  viewer: 'только чтение',
  none: 'вошёл, но ничего не видит',
};

export const LEVEL_LABELS: Record<AccessLevel, string> = {
  owner: 'Владелец',
  editor: 'Редактор',
  viewer: 'Просмотр',
  none: 'Нет доступа',
};

export interface User {
  id: string;
  discordId: string;
  name: string;
  avatarUrl: string | null;
  role: BaseRole;
  /** Роль выставлена админом вручную поверх той, что приходит из Discord. */
  roleOverride: boolean;
  mustChangePassword?: boolean;
}

export interface ColumnFields {
  colId: string;
  label: string;
  type: ColumnType;
  isFormula: boolean;
  formula: string;
  widgetOptions: { choices?: string[] } | null;
  position: number;
}

export interface Column {
  id: string;
  fields: ColumnFields;
}

export interface Rec {
  id: string;
  fields: Record<string, unknown>;
}

export interface Doc {
  id: string;
  name: string;
  icon: string | null;
  sectionId: string | null;
  tableCount: number;
  shareLinks: number;
  published: boolean;
  updatedAt: string;
}

export interface Section {
  id: string;
  name: string;
  position: number;
  docCount: number;
}

export interface TableRef {
  id: string;
  label: string;
  position: number;
  canEdit?: boolean;
  hasCustomAccess?: boolean;
  editorRoleId?: string | null;
  editorRoleName?: string | null;
}

export interface TableEditor {
  grantId: string;
  userId: string;
  name: string;
  avatarUrl: string | null;
  login?: string;
  level: AccessLevel;
}

export interface TableAccessSummaryItem {
  id: string;
  label: string;
  editorRoleId: string | null;
  editorRoleName: string | null;
  editorsCount: number;
}

export interface TableAccessInfo {
  tableId: string;
  label: string;
  editorRoleId: string | null;
  editorRoleName: string | null;
  editors: TableEditor[];
  allDocTables?: TableAccessSummaryItem[];
}

export interface Revision {
  id: string;
  op: 'insert' | 'update' | 'delete' | 'restore';
  recordId: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  at: string;
  actor: { id: string; name: string } | null;
}

export interface TableHistory {
  /** Подписи колонок на момент запроса, включая уже удалённые. */
  columns: { id: string; label: string }[];
  revisions: Revision[];
}

export interface MatrixUser {
  id: string;
  login?: string;
  name: string;
  avatarUrl: string | null;
  role: BaseRole;
  /** Роль выставлена админом вручную поверх базовой. */
  roleOverride: boolean;
  lastSeenAt: string | null;
}

export interface AccessMatrix {
  users: MatrixUser[];
  sections: { id: string; name: string; docCount: number }[];
  docs: { id: string; name: string; icon: string | null; sectionId: string | null }[];
  sectionAccess: { sectionId: string; userId: string; level: AccessLevel }[];
  docAccess: { userId: string; docId: string; docName: string; level: AccessLevel }[];
}

export interface PublicTable {
  id: string;
  label: string;
  columns: Column[];
  records: Rec[];
  styles: { recordId: string; colId: string; style: CellStyle }[];
  merges: Merge[];
  conditional: ConditionalRule[];
  sizes: TableSizes;
}

export interface PublicDoc {
  kind: 'snapshot' | 'live';
  name: string;
  icon: string | null;
  versionName?: string;
  createdAt?: string;
  tables: PublicTable[];
}

/**
 * Оформление ячейки. Ключи короткие, как в оригинале: флаги — единицей,
 * отсутствие — undefined. Менять имена нельзя: в базе уже лежат такие.
 */
export interface CellStyle {
  /** bold */ b?: 1;
  /** italic */ i?: 1;
  /** underline */ u?: 1;
  /** strikethrough */ s?: 1;
  /** цвет текста */ fg?: string;
  /** цвет заливки */ bg?: string;
  /** выравнивание по горизонтали */ h?: 'left' | 'center' | 'right';
  /** выравнивание по вертикали */ v?: 'top' | 'middle' | 'bottom';
  /** перенос текста */ w?: 1;
  /** шрифт (font-family) */ ff?: string;
  /** размер шрифта (px) */ fs?: number;
}

export type ConditionOp =
  | 'gt' | 'lt' | 'eq' | 'ne'
  | 'contains' | 'notcontains'
  | 'between' | 'empty' | 'notempty';

/** Порядок как в оригинале — он же порядок в выпадающем списке. */
export const CONDITIONS: { value: ConditionOp; label: string; hasValue: boolean; hasValue2?: boolean }[] = [
  { value: 'gt', label: 'Больше', hasValue: true },
  { value: 'lt', label: 'Меньше', hasValue: true },
  { value: 'eq', label: 'Равно', hasValue: true },
  { value: 'ne', label: 'Не равно', hasValue: true },
  { value: 'contains', label: 'Содержит', hasValue: true },
  { value: 'notcontains', label: 'Не содержит', hasValue: true },
  { value: 'between', label: 'Между', hasValue: true, hasValue2: true },
  { value: 'empty', label: 'Пусто', hasValue: false },
  { value: 'notempty', label: 'Не пусто', hasValue: false },
];

export interface ConditionalRule {
  id?: string;
  colId: string | null;
  op: ConditionOp;
  value?: string | null;
  value2?: string | null;
  /** Меньше — выше по приоритету. Первое совпавшее правило побеждает. */
  position?: number;
  style: CellStyle;
}

/** Объединение привязано к идентификаторам, а не к координатам сетки. */
export interface Merge {
  id?: string;
  topRecordId: string;
  leftColId: string;
  rowspan: number;
  colspan: number;
}

export interface ColWidth { colId: string; width: number | null }
export interface RowHeight { recordId: string; height: number | null }

export interface TableSizes {
  colWidths: ColWidth[];
  rowHeights: RowHeight[];
}

export type FeedbackKind = 'bug' | 'suggestion';
export type FeedbackStatus = 'open' | 'reviewed' | 'rejected';

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: 'На рассмотрении',
  reviewed: 'Рассмотрено',
  rejected: 'Отказано',
};

export interface Feedback {
  id: string;
  userId: string | null;
  kind: FeedbackKind;
  title: string;
  body: string;
  status: FeedbackStatus;
  archived: boolean;
  createdAt: string;
  author: { id: string; name: string; avatarUrl: string | null } | null;
}

/** Ошибка приходит как { error } — ровно этого ждал исходный клиент. */
async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) };
  if (init?.body && !headers['Content-Type']) headers['Content-Type'] = 'application/json';

  const res = await fetch(url, { credentials: 'same-origin', ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) throw new Error((data && data.error) || `${res.status} ${res.statusText}`);
  return data as T;
}

export const api = {
  // ---- сессия ----
  me: () => req<{ user: User }>('/api/auth/me').then((r) => r.user),
  authLogin: (data: { login: string; password?: string }) => req<{ ok: true; user: any }>('/api/auth/login', { method: 'POST', body: JSON.stringify(data) }),
  authLoginCode: (data: { code: string }) => req<{ ok: true; user: any }>('/api/auth/login-code', { method: 'POST', body: JSON.stringify(data) }),

  /** ВРЕМЕННОЕ: вход админом без Discord. Убрать вместе с кнопкой на входе. */
  authConfig: () => req<{ devLogin: boolean }>('/api/auth/config'),
  devLogin: () => req<{ ok: true }>('/api/auth/dev-login', { method: 'POST' }),
  logout: () => req('/api/auth/logout', { method: 'POST' }),
  changeCredentials: (data: { newPassword?: string; newLogin?: string }) => 
    req<{ ok: true }>('/api/auth/change-credentials', { method: 'POST', body: JSON.stringify(data) }),

  // ---- структура ----
  workspaces: (org = 'redwoodgta5rp') =>
    req<{ org: string; workspaces: Section[]; docs: Doc[] }>(
      `/api/orgs/${encodeURIComponent(org)}/workspaces`,
    ),
  docs: () => req<{ docs: Doc[] }>('/api/docs').then((r) => r.docs),
  docMeta: (docId: string) =>
    req<{
      id: string;
      name: string;
      icon: string | null;
      section: { id: string; name: string } | null;
      tables: TableRef[];
      canEdit?: boolean;
      canManageAccess?: boolean;
    }>(`/api/docs/${docId}`),
  createDoc: (name: string, icon?: string, sectionId?: string) =>
    req<Doc>('/api/docs', { method: 'POST', body: JSON.stringify({ name, icon, sectionId }) }),
  updateDoc: (docId: string, patch: { name?: string; icon?: string; sectionId?: string | null }) =>
    req<Doc>(`/api/docs/${docId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteDoc: (docId: string) => req(`/api/docs/${docId}`, { method: 'DELETE' }),
  duplicateDoc: (docId: string, body?: { name?: string }) =>
    req<Doc>(`/api/docs/${docId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  sections: () => req<{ sections: Section[] }>('/api/sections').then((r) => r.sections),
  sectionCreate: (name: string) =>
    req<Section>('/api/sections', { method: 'POST', body: JSON.stringify({ name }) }),
  sectionUpdate: (id: string, patch: { name?: string; position?: number }) =>
    req<Section>(`/api/sections/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  sectionDelete: (id: string) => req(`/api/sections/${id}`, { method: 'DELETE' }),

  // ---- таблицы и колонки ----
  tables: (docId: string) =>
    req<{ tables: TableRef[] }>(`/api/docs/${docId}/tables`).then((r) => r.tables),
  createTable: (docId: string, label: string) =>
    req<TableRef>(`/api/docs/${docId}/tables`, { method: 'POST', body: JSON.stringify({ label }) }),
  duplicateTable: (docId: string, tableId: string, body?: { label?: string }) =>
    req<TableRef>(`/api/docs/${docId}/tables/${tableId}/duplicate`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  updateTable: (docId: string, tableId: string, patch: { label?: string; position?: number }) =>
    req<TableRef>(`/api/docs/${docId}/tables/${tableId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteTable: (docId: string, tableId: string) =>
    req(`/api/docs/${docId}/tables/${tableId}`, { method: 'DELETE' }),

  tableAccess: (docId: string, tableId: string) =>
    req<TableAccessInfo>(`/api/docs/${docId}/tables/${tableId}/access`),
  tableAddEditor: (
    docId: string,
    tableId: string,
    body: { userId: string },
  ) =>
    req<TableEditor>(`/api/docs/${docId}/tables/${tableId}/access/users`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  tableRemoveEditor: (docId: string, tableId: string, userId: string) =>
    req(`/api/docs/${docId}/tables/${tableId}/access/users/${userId}`, { method: 'DELETE' }),
  tableSetEditorRole: (
    docId: string,
    tableId: string,
    body: { roleId: string | null; roleName?: string | null },
  ) =>
    req<{ id: string; editorRoleId: string | null; editorRoleName: string | null }>(
      `/api/docs/${docId}/tables/${tableId}/access/role`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),

  columns: (docId: string, tableId: string) =>
    req<{ columns: Column[] }>(`/api/docs/${docId}/tables/${tableId}/columns`)
      .then((r) => r.columns),
  createColumn: (
    docId: string,
    tableId: string,
    body: Partial<ColumnFields> & { position?: number },
  ) =>
    req<Column>(`/api/docs/${docId}/tables/${tableId}/columns`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reorderColumns: (docId: string, tableId: string, colIds: string[]) =>
    req<{ columns: Column[] }>(`/api/docs/${docId}/tables/${tableId}/columns/reorder`, {
      method: 'POST',
      body: JSON.stringify({ colIds }),
    }).then((r) => r.columns),
  updateColumn: (docId: string, tableId: string, colId: string, patch: Partial<ColumnFields>) =>
    req<Column>(`/api/docs/${docId}/tables/${tableId}/columns/${colId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteColumn: (docId: string, tableId: string, colId: string) =>
    req(`/api/docs/${docId}/tables/${tableId}/columns/${colId}`, { method: 'DELETE' }),

  // ---- записи ----
  records: (
    docId: string,
    tableId: string,
    opts?: { limit?: number; filter?: Record<string, unknown[]> },
  ) => {
    const params = new URLSearchParams();
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.filter) params.set('filter', JSON.stringify(opts.filter));
    const qs = params.toString();
    return req<{ records: Rec[] }>(
      `/api/docs/${docId}/tables/${tableId}/records${qs ? `?${qs}` : ''}`,
    ).then((r) => r.records);
  },
  createRecords: (docId: string, tableId: string, records: { fields: Record<string, unknown> }[], afterId?: string) =>
    req<{ records: Rec[] }>(`/api/docs/${docId}/tables/${tableId}/records`, {
      method: 'POST',
      body: JSON.stringify({ records, afterId }),
    }),
  patchRecords: (docId: string, tableId: string, records: Rec[]) =>
    req<{ records: Rec[] }>(`/api/docs/${docId}/tables/${tableId}/records`, {
      method: 'PATCH',
      body: JSON.stringify({ records }),
    }),
  deleteRecords: (docId: string, tableId: string, ids: string[]) =>
    req<{ deleted: number }>(`/api/docs/${docId}/tables/${tableId}/data/delete`, {
      method: 'POST',
      body: JSON.stringify(ids),
    }),

  importRows: (
    docId: string,
    tableId: string,
    body: {
      rows: unknown[][];
      mode: 'append' | 'replace';
      hasHeader: boolean;
      preserveDesign?: boolean;
    },
  ) =>
    req<{ imported: number; columns: number }>(
      `/api/docs/${docId}/tables/${tableId}/import`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  exportCsvUrl: (docId: string, tableId: string) =>
    `/api/docs/${docId}/tables/${tableId}/export.csv`,

  // ---- история ----
  tableHistory: (docId: string, tableId: string, limit = 50) =>
    req<TableHistory>(`/api/docs/${docId}/tables/${tableId}/history?limit=${limit}`),

  // ---- оформление ----
  cellStyles: (docId: string, tableId: string) =>
    req<{ styles: { recordId: string; colId: string; style: CellStyle }[] }>(
      `/api/docs/${docId}/tables/${tableId}/styles`,
    ).then((r) => r.styles),
  patchCellStyles: (
    docId: string,
    tableId: string,
    updates: { recordId: string; colId: string; style: CellStyle | null }[],
  ) =>
    req(`/api/docs/${docId}/tables/${tableId}/styles`, {
      method: 'PATCH',
      body: JSON.stringify({ updates }),
    }),
  cellMerges: (docId: string, tableId: string) =>
    req<{ merges: Merge[] }>(`/api/docs/${docId}/tables/${tableId}/merges`).then((r) => r.merges),
  patchCellMerges: (
    docId: string,
    tableId: string,
    body: {
      merges?: Omit<Merge, 'id'>[];
      unmerges?: { topRecordId: string; leftColId: string }[];
    },
  ) =>
    req(`/api/docs/${docId}/tables/${tableId}/merges`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  tableSizes: (docId: string, tableId: string) =>
    req<TableSizes>(`/api/docs/${docId}/tables/${tableId}/sizes`),
  patchTableSizes: (
    docId: string,
    tableId: string,
    body: { colWidths?: ColWidth[]; rowHeights?: RowHeight[] },
  ) =>
    req(`/api/docs/${docId}/tables/${tableId}/sizes`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  conditionalRules: (docId: string, tableId: string) =>
    req<{ rules: ConditionalRule[] }>(`/api/docs/${docId}/tables/${tableId}/conditional`)
      .then((r) => r.rules),
  /** upsert создаёт и обновляет, remove удаляет по идентификаторам. */
  patchConditionalRules: (
    docId: string,
    tableId: string,
    body: { upsert: ConditionalRule[]; remove: string[] },
  ) =>
    req(`/api/docs/${docId}/tables/${tableId}/conditional`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  // ---- доступ ----
  access: () => req<AccessMatrix>('/api/access'),
  searchUsers: (q: string) =>
    req<{ members: Array<{ id: string; name: string; username: string; avatarUrl: string | null }> }>(
      `/api/access/users/search?q=${encodeURIComponent(q)}`,
    ),
  discordRoles: () =>
    req<{ roles: Array<{ id: string; name: string; color: string | null; position: number }> }>(
      '/api/access/discord-roles',
    ).then((r) => r.roles),
  accessAddUser: (data: { userId?: string; login?: string; password?: string; name?: string; role: BaseRole }) =>
    req<{ id: string; name: string; resolved: boolean }>('/api/access/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  accessRemoveUser: (userId: string) => req(`/api/access/users/${userId}`, { method: 'DELETE' }),
  /** null возвращает роль к той, что приходит из Discord. */
  accessSetRole: (userId: string, role: BaseRole | null) =>
    req(`/api/access/users/${userId}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  accessSetSection: (sectionId: string, userId: string, level: AccessLevel | null) =>
    req(`/api/access/sections/${sectionId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ level }),
    }),
  accessSetDoc: (docId: string, userId: string, level: AccessLevel | null) =>
    req(`/api/access/docs/${docId}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ level }),
    }),

  // ---- обращения ----
  feedbackList: () => req<{ items: Feedback[]; isAdmin: boolean }>('/api/feedback'),
  feedbackCreate: (kind: FeedbackKind, title: string, body: string) =>
    req<Feedback>('/api/feedback', { method: 'POST', body: JSON.stringify({ kind, title, body }) }),
  feedbackSetStatus: (id: string, status: FeedbackStatus) =>
    req(`/api/feedback/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  feedbackSetArchived: (id: string, archived: boolean) =>
    req(`/api/feedback/${id}`, { method: 'PATCH', body: JSON.stringify({ archived }) }),
  feedbackDelete: (id: string) => req(`/api/feedback/${id}`, { method: 'DELETE' }),

  // ---- публикация ----
  shareCreate: (docId: string, config: Record<string, unknown>) =>
    req<{ token: string }>(`/api/docs/${docId}/share`, {
      method: 'POST',
      body: JSON.stringify({ config }),
    }),
  shareList: (docId: string) =>
    req<{ links: { id: string; token: string; url: string; createdAt: string }[] }>(
      `/api/docs/${docId}/share`,
    ).then((r) => r.links),
  shareDelete: (token: string) => req(`/api/share/${token}`, { method: 'DELETE' }),

  publicVersionCreate: (docId: string, body: { name?: string }) =>
    req<{ token: string }>(`/api/docs/${docId}/public`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  publicVersionList: (docId: string) =>
    req<{ versions: { id: string; name: string; token: string }[] }>(`/api/docs/${docId}/public`)
      .then((r) => r.versions),
  publicVersionUpdate: (
    docId: string,
    versionId: string,
    patch: { name?: string; refresh?: boolean },
  ) =>
    req(`/api/docs/${docId}/public/${versionId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  publicVersionDelete: (docId: string, versionId: string) =>
    req(`/api/docs/${docId}/public/${versionId}`, { method: 'DELETE' }),

  // ---- открытые маршруты ----
  publicDoc: (token: string) => req<PublicDoc>(`/api/public/${token}`),
  sharedDoc: (token: string) => req<PublicDoc>(`/api/shared/${token}`),

  uploadImage: async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch('/api/upload', {
      method: 'POST',
      body: form,
      credentials: 'same-origin',
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) throw new Error((data && data.error) || `${res.status}`);
    return data as { id: string; url: string; size: number };
  },

  // ---- портал государственных реестров ----
  portalGet: () => req<PublicPortalResponse>('/api/portal'),
  portalAdminGet: () => req<PortalAdminResponse>('/api/portal/admin'),
  portalCreateWidget: (body: {
    docId: string;
    tableId?: string;
    title: string;
    description?: string;
    category?: string;
    icon?: string;
    isPinned?: boolean;
    enabled?: boolean;
    previewRows?: boolean;
  }) => req<PortalWidgetData>('/api/portal/admin/widgets', { method: 'POST', body: JSON.stringify(body) }),
  portalUpdateWidget: (id: string, body: Record<string, unknown>) =>
    req<PortalWidgetData>(`/api/portal/admin/widgets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  portalDeleteWidget: (id: string) =>
    req<{ ok: true }>(`/api/portal/admin/widgets/${id}`, { method: 'DELETE' }),
  portalReorderWidgets: (ids: string[]) =>
    req<{ ok: true }>('/api/portal/admin/widgets/reorder', { method: 'PUT', body: JSON.stringify({ ids }) }),
  portalUpdateConfig: (body: { title?: string; subtitle?: string; badge?: string }) =>
    req<PortalConfigData>('/api/portal/admin/config', { method: 'PATCH', body: JSON.stringify(body) }),
};

export interface PortalConfigData {
  id: string;
  title: string;
  subtitle: string | null;
  badge: string | null;
  updatedAt: string;
}

export interface PortalWidgetData {
  id: string;
  title: string;
  description: string | null;
  category: string;
  icon: string | null;
  docId: string;
  tableId: string | null;
  shareToken: string;
  position: number;
  isPinned: boolean;
  enabled: boolean;
  previewRows: boolean;
  updatedAt: string;
  stats?: {
    rowCount: number;
    colCount: number;
    sheetName: string;
  };
  preview?: {
    columns: Array<{ colId: string; label: string; type: string }>;
    rows: Array<{ id: string; fields: Record<string, unknown> }>;
  };
}

export interface PublicPortalResponse {
  config: PortalConfigData;
  categories: string[];
  widgets: PortalWidgetData[];
  totalRegistries: number;
  totalRecords: number;
}

export interface PortalAdminResponse {
  config: PortalConfigData;
  widgets: PortalWidgetData[];
  availableDocs: Array<{
    id: string;
    name: string;
    icon: string | null;
    tables: Array<{ id: string; label: string }>;
  }>;
}

